// ============================================================================
// OnboardingCard — per-person Kanban card. Progress bar + top-2 blocking
// items + days-since-start + type chip + priority badge. Spec §2 (card).
// ============================================================================
import { GitBranch } from 'lucide-react';
import { Card } from '../../shared/components';
import {
  effectiveChecklist,
  computeProgress,
  blockingItems,
  computePriority,
  daysSinceStart,
  fmtDateShort,
} from './utils';
import { TYPE_CHIP_LABELS, checklistLabel, type OnboardingRecord } from './types';

const TYPE_CHIP_CLASS: Record<string, string> = {
  W2: 'bg-st-info-bg-strong text-st-info-fg-strong',
  Partner: 'bg-violet-100 text-violet-800',
  Subcontractor: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
};

export default function OnboardingCard({
  rec,
  onClick,
}: {
  rec: OnboardingRecord;
  onClick: (rec: OnboardingRecord) => void;
}) {
  const checklist = effectiveChecklist(rec.checklist);
  const progress = computeProgress(checklist, rec.employmentTypeForChecklist);
  const blockers = blockingItems(checklist, rec.employmentTypeForChecklist, 2);
  const priority = computePriority(rec.start_date, progress);
  const sinceStart = daysSinceStart(rec.start_date);

  // N3 — sub-onboarding portal state from checklist_json.portal (extra key,
  // stamped by the sub-onboard edge fn): issued but not completed = the ball
  // is in the sub's court.
  const portal = rec.rawChecklistJson?.portal as
    | { issued_at?: string; completed_at?: string }
    | undefined;
  const portalChip = portal?.completed_at
    ? ('complete' as const)
    : portal?.issued_at
      ? ('awaiting' as const)
      : null;

  return (
    <Card
      interactive
      className="cursor-pointer p-3"
      role="button"
      aria-label={rec.type === 'W2' ? `Open onboarding for ${rec.name}` : `Open subcontractor setup card for ${rec.name}`}
      onClick={() => onClick(rec)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-tcs-text">{rec.name}</div>
          {(rec.role || rec.account) && (
            <div className="mt-0.5 truncate text-xs text-tcs-text-muted">
              {[rec.role, rec.account].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
            TYPE_CHIP_CLASS[rec.type] ?? 'bg-tcs-surface text-tcs-text-secondary'
          }`}
        >
          {TYPE_CHIP_LABELS[rec.type]}
        </span>
      </div>

      {portalChip && (
        <span
          className={`mt-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            portalChip === 'complete'
              ? 'bg-st-ok-bg-strong text-st-ok-fg-strong'
              : 'bg-st-warn-bg-strong text-st-warn-fg-strong'
          }`}
        >
          {portalChip === 'complete' ? 'Portal complete' : 'Awaiting sub'}
        </span>
      )}

      <div className="mt-1.5 text-[11px] text-tcs-text-muted">
        {rec.start_date ? (
          <>
            Start: {fmtDateShort(rec.start_date)}
            {sinceStart !== null && (
              <> · {sinceStart >= 0 ? `Day ${sinceStart + 1}` : `Starts in ${-sinceStart}d`}</>
            )}
          </>
        ) : (
          'No start date'
        )}
      </div>

      {/* Progress bar */}
      <div className="mt-2 flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-tcs-surface">
          <div
            className="h-full rounded-full bg-st-ok-solid transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="shrink-0 text-[11px] tabular-nums text-tcs-text-secondary">
          {progress}%
        </span>
      </div>

      {/* Top-2 blocking items */}
      {blockers.length > 0 ? (
        <ul className="mt-2 space-y-0.5">
          {blockers.map((b) => (
            <li key={b.key} className="truncate text-[11px] text-st-danger-fg">
              {b.status === 'blocked' ? '⏳' : '❌'} {checklistLabel(b.key, b.label, rec.employmentTypeForChecklist)}
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-2 text-[11px] text-st-ok-fg">✅ No blocking items</div>
      )}

      <div className="mt-2 flex items-center justify-between">
        {rec.hiring_request_id && (
          <span
            className="inline-flex items-center gap-1 text-[10px] text-tcs-text-muted"
            title={`From hiring request ${rec.hiring_request_id}`}
          >
            <GitBranch className="h-3 w-3" /> {rec.hiring_request_id}
          </span>
        )}
        <span
          className={`ml-auto rounded px-1.5 py-0.5 text-[10px] font-bold ${
            priority === 'HIGH' ? 'bg-st-danger-bg-strong text-st-danger-fg' : 'bg-tcs-surface text-tcs-text-secondary'
          }`}
        >
          {priority}
        </span>
      </div>
    </Card>
  );
}
